/**
 * <story-viewer> — lecteur de stories UGC plein écran (§7.6), Web Component natif, zéro dépendance.
 *
 * Amélioration progressive : sans ce script, les bulles restent des liens (ou des vignettes
 * statiques) — aucune régression. Avec le script, un tap sur une bulle ouvre un <dialog>
 * plein écran façon stories : média vertical, barres de progression, précédent/suivant
 * (boutons, zones de tap, flèches clavier, swipe), avance automatique, et un appel à l'action
 * optionnel. Le piège de focus, la touche Échap et le fond assombri sont fournis gratuitement
 * par dialog.showModal() — même mécanique que le panier et la galerie produit du thème.
 *
 * Ce N'EST PAS Tolstoy : on ne lit que nos propres médias déjà hébergés sur le CDN Shopify.
 * Aucune couche shoppable, aucun import réseau social, aucun script tiers.
 */

const IMAGE_DEFAULT_MS = 5000; // Repli si la durée d'image n'est pas fournie par la section.

class StoryViewer extends HTMLElement {
  connectedCallback() {
    this.dialog = this.querySelector('[data-story-dialog]');
    this.triggers = Array.from(this.querySelectorAll('[data-story-trigger]'));

    const dataEl = this.querySelector('[data-story-data]');
    try {
      this.slides = dataEl ? JSON.parse(dataEl.textContent) : [];
    } catch {
      this.slides = [];
    }
    if (!this.dialog || !this.slides.length || !this.triggers.length) return;

    // Références internes au dialog (fournies par le snippet story-viewer-dialog).
    this.mediaEl = this.dialog.querySelector('[data-story-media]');
    this.stageEl = this.dialog.querySelector('[data-story-stage]');
    this.progressEl = this.dialog.querySelector('[data-story-progress]');
    this.captionEl = this.dialog.querySelector('[data-story-caption]');
    this.ctaEl = this.dialog.querySelector('[data-story-cta]');
    this.soundBtn = this.dialog.querySelector('[data-story-sound]');
    // Coque incomplète (markup divergent) → on n'active pas plutôt que de casser au 1er open().
    if (!this.mediaEl || !this.progressEl || !this.captionEl || !this.ctaEl) return;
    // parseFloat : une durée décimale (ex. « 4.8 ») ne doit pas être tronquée par parseInt.
    this.imageMs = parseFloat(this.getAttribute('data-image-duration')) * 1000 || IMAGE_DEFAULT_MS;

    this.reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.index = 0;
    this.rafId = null;
    this.startTime = 0;
    this.elapsed = 0;
    this.paused = false;
    this.muted = true;
    this.lastTrigger = null;

    // Le JS est là : on active les affordances réservées (curseur, ouverture au tap).
    this.classList.add('story-viewer--enhanced');

    this.triggers.forEach((trigger, i) => {
      trigger.addEventListener('click', (event) => {
        event.preventDefault();
        this.open(i);
      });
    });

    this.#bindDialogControls();
  }

  #bindDialogControls() {
    // Fermeture : bouton dédié + clic sur le fond assombri.
    this.dialog.querySelector('[data-story-close]')?.addEventListener('click', () => this.dialog.close());
    this.dialog.addEventListener('click', (event) => {
      if (event.target === this.dialog) this.dialog.close();
    });
    // Nettoyage systématique quelle que soit la façon de fermer (Échap inclus).
    this.dialog.addEventListener('close', () => this.#teardown());

    // Zones de tap = boutons précédent / suivant (clavier-accessibles).
    this.dialog.querySelector('[data-story-prev]')?.addEventListener('click', () => this.prev());
    this.dialog.querySelector('[data-story-next]')?.addEventListener('click', () => this.next());
    this.soundBtn?.addEventListener('click', () => this.#toggleSound());

    // Flèches clavier (showModal fournit déjà Échap et le piège de focus).
    this.dialog.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowRight') { event.preventDefault(); this.next(); }
      else if (event.key === 'ArrowLeft') { event.preventDefault(); this.prev(); }
    });

    // Sur toute la scène : appui maintenu = pause ; swipe horizontal = navigation.
    // Les listeners sont posés sur la scène (les zones-boutons sont par-dessus, l'évènement remonte).
    this.swiped = false;
    this.stageEl?.addEventListener('pointerdown', (event) => {
      this.pointerStartX = event.clientX;
      this.pointerStartTime = event.timeStamp;
      this.swiped = false;
      this.#pause();
    });
    this.stageEl?.addEventListener('pointerup', (event) => {
      const dx = event.clientX - (this.pointerStartX ?? event.clientX);
      if (Math.abs(dx) > 40) {
        this.swiped = true; // le clic de zone qui suit sera neutralisé
        dx < 0 ? this.next() : this.prev();
      } else {
        this.#resume(); // tap court ou fin d'appui maintenu → reprise (la nav éventuelle vient du clic de zone)
      }
    });
    this.stageEl?.addEventListener('pointercancel', () => this.#resume());
    // Un swipe ne doit pas AUSSI déclencher le clic de la zone (double navigation).
    this.stageEl?.addEventListener('click', (event) => {
      if (this.swiped) {
        event.preventDefault();
        event.stopPropagation();
        this.swiped = false;
      }
    }, true);

    // Onglet masqué → on met en pause proprement. Handler nommé pour pouvoir le
    // retirer au disconnect (sinon fuite d'écouteur global, cf. revue 2026-07-06).
    this._onVisibility = () => {
      if (!this.dialog.open) return;
      document.hidden ? this.#pause() : this.#resume();
    };
    document.addEventListener('visibilitychange', this._onVisibility);
  }

  disconnectedCallback() {
    if (this._onVisibility) document.removeEventListener('visibilitychange', this._onVisibility);
    this.#stopTimers();
    this.video?.pause();
  }

  open(i) {
    this.lastTrigger = this.triggers[i] || document.activeElement;
    this.index = i;
    this.#buildProgress();
    this.dialog.showModal();
    this.#render();
  }

  next() {
    if (this.index >= this.slides.length - 1) {
      this.dialog.close(); // dernière story terminée → fermeture
      return;
    }
    this.index += 1;
    this.#render();
  }

  prev() {
    if (this.index <= 0) return;
    this.index -= 1;
    this.#render();
  }

  // ---- Rendu d'une story ----
  #render() {
    this.#stopTimers();
    this.elapsed = 0;
    this.paused = false;
    const slide = this.slides[this.index];
    if (!slide) return;

    this.#updateProgressStates();

    // Média : on repart d'un conteneur propre à chaque story.
    this.mediaEl.replaceChildren();
    this.video = null;

    if (slide.kind === 'video' && slide.sources?.length) {
      const video = document.createElement('video');
      video.className = 'story-viewer__video';
      video.playsInline = true;
      video.muted = this.muted;
      video.setAttribute('playsinline', '');
      video.preload = 'auto';
      if (slide.poster) video.poster = slide.poster;
      slide.sources.forEach((src) => {
        const source = document.createElement('source');
        source.src = src.url;
        if (src.type) source.type = src.type;
        video.appendChild(source);
      });
      this.mediaEl.appendChild(video);
      this.video = video;
      if (this.soundBtn) this.soundBtn.hidden = false;
      this.#reflectSound();
      video.addEventListener('ended', () => this.next());
      video.play().catch(() => {}); // l'autoplay muet est autorisé ; on ignore un éventuel refus
    } else {
      const img = document.createElement('img');
      img.className = 'story-viewer__image';
      img.src = slide.poster || '';
      img.alt = slide.alt || '';
      this.mediaEl.appendChild(img);
      if (this.soundBtn) this.soundBtn.hidden = true;
    }

    // Légende + appel à l'action optionnel.
    this.captionEl.textContent = slide.attribution || '';
    this.captionEl.hidden = !slide.attribution;
    if (slide.link) {
      this.ctaEl.href = slide.link;
      this.ctaEl.textContent = slide.linkLabel || this.ctaEl.dataset.defaultLabel || '';
      this.ctaEl.hidden = false;
    } else {
      this.ctaEl.hidden = true;
    }

    // Le focus part sur la zone « suivant » pour une navigation clavier immédiate.
    this.dialog.querySelector('[data-story-next]')?.focus({ preventScroll: true });

    this.#startProgress();
  }

  // ---- Barres de progression ----
  #buildProgress() {
    this.progressEl.replaceChildren();
    this.segments = this.slides.map(() => {
      const seg = document.createElement('span');
      seg.className = 'story-viewer__segment';
      const fill = document.createElement('span');
      fill.className = 'story-viewer__segment-fill';
      seg.appendChild(fill);
      this.progressEl.appendChild(seg);
      return fill;
    });
  }

  #updateProgressStates() {
    this.segments?.forEach((fill, i) => {
      fill.style.transform = i < this.index ? 'scaleX(1)' : 'scaleX(0)';
    });
  }

  #startProgress() {
    if (this.reduceMotion) return; // pas d'avance auto : navigation 100 % manuelle
    this.startTime = performance.now();
    this.elapsed = 0;
    this.#tick();
  }

  #tick() {
    const fill = this.segments?.[this.index];
    if (!fill) return;
    let fraction;
    if (this.video && this.video.duration) {
      fraction = this.video.currentTime / this.video.duration;
    } else {
      const now = performance.now();
      fraction = (this.elapsed + (now - this.startTime)) / this.imageMs;
    }
    fill.style.transform = `scaleX(${Math.min(fraction, 1)})`;
    if (fraction >= 1) {
      this.next();
      return;
    }
    this.rafId = requestAnimationFrame(() => this.#tick());
  }

  // ---- Pause / reprise ----
  #pause() {
    if (this.paused || this.reduceMotion) return;
    this.paused = true;
    if (this.rafId) cancelAnimationFrame(this.rafId);
    this.rafId = null;
    if (this.video) this.video.pause();
    else this.elapsed += performance.now() - this.startTime;
  }

  #resume() {
    if (!this.paused || this.reduceMotion || !this.dialog.open) return;
    this.paused = false;
    if (this.video) this.video.play().catch(() => {});
    else this.startTime = performance.now();
    this.#tick();
  }

  // ---- Son ----
  #toggleSound() {
    this.muted = !this.muted;
    if (this.video) this.video.muted = this.muted;
    this.#reflectSound();
  }

  #reflectSound() {
    if (!this.soundBtn) return;
    this.soundBtn.classList.toggle('story-viewer__sound--muted', this.muted);
    const label = this.muted ? this.soundBtn.dataset.labelUnmute : this.soundBtn.dataset.labelMute;
    if (label) this.soundBtn.setAttribute('aria-label', label);
    this.soundBtn.setAttribute('aria-pressed', String(!this.muted));
  }

  // ---- Nettoyage ----
  #stopTimers() {
    if (this.rafId) cancelAnimationFrame(this.rafId);
    this.rafId = null;
  }

  #teardown() {
    this.#stopTimers();
    if (this.video) {
      this.video.pause();
      this.video = null;
    }
    this.mediaEl?.replaceChildren();
    // Rendre le focus à la bulle d'origine (accessibilité).
    this.lastTrigger?.focus?.({ preventScroll: true });
  }
}

if (!customElements.get('story-viewer')) {
  customElements.define('story-viewer', StoryViewer);
}
